import React from 'react';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { StaffingRequestBar } from 'works/components/staffing-request-bar';
import { ApplicationProfile } from 'works/pages/organization/applications/application-profile';
import { OfferForm } from 'works/components/offer-form';
import { ErrorHandler, LoadingState } from '@common/components';
import { gql, useQuery, useLocation, useParams } from '@common/hooks';
import { HistoryInfoFragment } from '@common/graphql';
import { OFFER_INFO_DETAILS_FRAGMENT } from '@applicant-flow/graphql/fragments';

const STAFFING_REQUEST_APPLICATION_NAME = gql`
  query StaffingRequestApplicationData($id: ID!) {
    staffingRequestApplication(id: $id) {
      id
      employmentType
      rejectionReason
      doNotRehire
      doNotRehireComment
      profileStatusHistory {
        ...HistoryInfoFragment
      }
      ...StaffingRequestApplicationProfileInfo
      ...StaffingRequestMakeOfferInfo
      organization {
        id
        name
      }
      organizationUnit {
        id
        name
      }
      organizationBusiness {
        id
        name
      }
      staffingRequest {
        id
        name
        staffingRequestType
        ...StaffingRequestBarFragment
      }
      organization {
        id
        name
      }
      activeOffer {
        id
        state
        stateChangedAt
        startDate
        rejectionReason
        rejectionDetails
        endDate
        billingRegularRate
        timeoffDescription
        shiftType
        shiftsPerWeek
        hoursPerShift
        createdAt
        staffingRequestAssignment {
          id
        }
        history {
          ...HistoryInfoFragment
        }
        # FIXME: fetch id to help with apollo cache
        # eslint-disable-next-line @graphql-eslint/require-id-when-available
        ...OfferDetailsInfo
      }
      activeOffers {
        id
        state
        rejectionReason
        rejectionDetails
        stateChangedAt
        startDate
        endDate
        billingRegularRate
        timeoffDescription
        shiftType
        shiftsPerWeek
        hoursPerShift
        createdAt
        history {
          ...HistoryInfoFragment
        }
        # FIXME: fetch id to help with apollo cache
        # eslint-disable-next-line @graphql-eslint/require-id-when-available
        ...OfferDetailsInfo
      }
      worksSubmittalPacket {
        id
        files {
          id
          filename
          downloadUrl
          createdAt
        }
      }
      worksProfile {
        id
        firstName
        lastName
        fullName
        identificationCode
      }
    }
  }
  ${ApplicationProfile.fragments.profileInfo}
  ${StaffingRequestBar.fragment}
  ${OfferForm.fragments.staffingRequestInfo}
  ${HistoryInfoFragment}
  ${OFFER_INFO_DETAILS_FRAGMENT}
`;

export function OrganizationCandidatePage() {
  const { id: staffingRequestApplicationId } = useParams();
  const location = useLocation();

  const isStaffingRequestRoute = location.pathname.includes(
    '/works/staffing-requests'
  );

  const { loading, error, data } = useQuery(STAFFING_REQUEST_APPLICATION_NAME, {
    variables: { id: staffingRequestApplicationId },
  });

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorHandler error={error} log="CandidatePage" />;
  }

  const application = data.staffingRequestApplication;
  const staffingRequest = application.staffingRequest;
  const worksProfile = application.worksProfile;
  const worksProfileFullName = worksProfile.fullName;

  const generateBreadCrumbParams = () => {
    if (isStaffingRequestRoute) {
      return [
        { name: 'Staffing', path: '/works/staffing-requests' },
        {
          name: application.staffingRequest.name,
          path: `/works/staffing-requests/${staffingRequest.id}`,
        },
        {
          name: worksProfileFullName,
          path: `/works/staffing-requests/${staffingRequest.id}/candidates/${staffingRequestApplicationId}`,
        },
      ];
    }

    return [
      { name: 'Applications', path: '/works/applications' },
      {
        name: worksProfileFullName,
        path: `/works/applications/${staffingRequestApplicationId}`,
      },
    ];
  };

  setBreadcrumbs(generateBreadCrumbParams());

  return (
    <React.Fragment>
      <StaffingRequestBar
        showDropDown={true}
        showState={false}
        staffingRequest={staffingRequest}
      />
      <ApplicationProfile application={application} />
    </React.Fragment>
  );
}
